import type { ConsentValue } from '../components/ConsentScreen'

/**
 * Die erteilte Einwilligung über ein Neuladen hinweg merken.
 *
 * Gemerkt wurde bisher nur **ob** jemand zugestimmt hat, nicht **wozu**. Nach
 * einem Neuladen sprang die App deshalb am Einwilligungsbildschirm vorbei
 * direkt in den Chat — aber Mikrofon und Kamera galten als nicht freigegeben.
 * Sprechen ging dann nicht mehr, und der einzige Ausweg war, den Browserspeicher
 * zu leeren, um wieder gefragt zu werden. Das ist das Gegenteil dessen, was die
 * Einwilligung leisten soll.
 *
 * **Bewusst `sessionStorage`, nicht `localStorage`:** Die Zustimmung gilt für
 * diesen Besuch, nicht für alle Zeit. Wer das Tablet im Lab weglegt und der
 * nächste Besucher nimmt es auf, wird neu gefragt — die Einwilligung ist an die
 * Person gebunden, nicht an das Gerät.
 */

const KEY = 'k4ia_ai_consent'

export function saveConsent(consent: ConsentValue): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(consent))
  } catch {
    // Privater Modus oder gesperrter Speicher: Dann wird eben erneut gefragt.
    // Ärgerlich, aber harmlos — und besser als ein Absturz.
  }
}

export function loadConsent(): ConsentValue | null {
  try {
    const roh = sessionStorage.getItem(KEY)
    if (!roh) return null
    // Die frühere Fassung schrieb hier schlicht "true". Ein Altbestand darf
    // nicht dazu führen, dass jemand im Chat landet, ohne dass eine Zustimmung
    // hinterlegt ist — dann lieber einmal zu viel fragen.
    if (roh === 'true') return null
    const gelesen = JSON.parse(roh) as Partial<ConsentValue>
    if (typeof gelesen?.ai !== 'boolean') return null
    return {
      ai: gelesen.ai,
      microphone: gelesen.microphone === true,
      camera: gelesen.camera === true,
    }
  } catch {
    return null
  }
}

export function clearConsent(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // s. o.
  }
}
